import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link } from 'react-router-dom'
import styled, { keyframes } from 'styled-components'
import { toast } from 'react-toastify'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded'
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded'
import EngineeringRoundedIcon from '@mui/icons-material/EngineeringRounded'
import EventRoundedIcon from '@mui/icons-material/EventRounded'
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded'
import UserPicker from 'features/taskManagement/components/UserPicker'
import FormTemplateField from 'features/taskManagement/components/FormTemplateField'
import { maskEmail } from 'utils/string'
import { getTask, assignTask, updateApprovers, updateTask, assignExternalUser, removeExternalAssignee } from 'services/taskService'
import { listContractors } from 'services/contractorService'
import SearchableSelect from 'components/SearchableSelect'

const toDateInput = value => {
  if (!value) return ''
  const s = String(value)
  return s.length >= 10 ? s.slice(0, 10) : s
}

const sameSet = (a, b) => a.length === b.length && a.every(x => b.includes(x))

const TaskQuickEditModal = ({ task, bpId, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [contractors, setContractors] = useState([])
  const [full, setFull] = useState(null)
  const [assigneeIds, setAssigneeIds] = useState([])
  const [approverIds, setApproverIds] = useState([])
  const [contractorId, setContractorId] = useState('')
  const [deadline, setDeadline] = useState('')
  const [formTemplateId, setFormTemplateId] = useState('')
  const [initialFormTemplateId, setInitialFormTemplateId] = useState('')
  const [hasAnswers, setHasAnswers] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [initialAssignees, setInitialAssignees] = useState([])
  const [initialApprovers, setInitialApprovers] = useState([])
  const [externalAssignees, setExternalAssignees] = useState([])
  const [extEmail, setExtEmail] = useState('')
  const [extName, setExtName] = useState('')
  const [invitingExt, setInvitingExt] = useState(false)

  useEffect(() => {
    let active = true
    setLoading(true)
    Promise.all([
      getTask(task.id),
      listContractors().catch(() => null),
    ])
      .then(([taskRes, contractorRes]) => {
        if (!active) return
        const t = taskRes?.data?.data || taskRes?.data
        const a = t?.assignees || []
        const r = t?.approvers || []
        const fid = t?.formTemplate?.id || ''
        setFull(t)
        setInitialAssignees(a)
        setInitialApprovers(r)
        setExternalAssignees(t?.externalAssignees || [])
        setAssigneeIds(a.map(u => u.id))
        setApproverIds(r.map(u => u.id))
        setContractorId(t?.contractorId || '')
        setDeadline(toDateInput(t?.deadline))
        setFormTemplateId(fid)
        setInitialFormTemplateId(fid)
        setHasAnswers(!!t?.formResponse?.responses && Object.keys(t.formResponse.responses).length > 0)
        setSelectedTemplate(t?.formTemplate || null)
        setContractors(contractorRes?.data?.data?.contractors || [])
      })
      .catch(() => { if (active) toast.error('Could not load this task. Please try again.') })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [task.id])

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const save = async () => {
    if (!full) return

    const origAssignees = (full.assignees || []).map(u => u.id)
    const assigneesChanged = !sameSet(assigneeIds, origAssignees)

    const cleanApprovers = approverIds
    const origApprovers = (full.approvers || []).map(u => u.id)
    const approversChanged = !sameSet(cleanApprovers, origApprovers)

    const patch = {}
    if ((contractorId || '') !== (full.contractorId || '')) patch.contractorId = contractorId || null
    if (deadline !== toDateInput(full.deadline)) patch.deadline = deadline || null
    const formChanged = (formTemplateId || '') !== (initialFormTemplateId || '')
    if (formChanged) patch.formTemplateId = formTemplateId || null

    if (!assigneesChanged && !approversChanged && Object.keys(patch).length === 0) {
      onClose()
      return
    }

    if (formChanged && initialFormTemplateId && hasAnswers) {
      const ok = window.confirm('Changing the form will clear the answers already entered on this task. Continue?')
      if (!ok) return
    }

    setSaving(true)
    const jobs = []
    const failed = []
    let serverMsg = null
    const grab = err => { serverMsg = serverMsg || err?.response?.data?.message || null }

    if (assigneesChanged) {
      jobs.push(assignTask({ taskId: full.id, assigneeIds }).catch(err => { failed.push('assignees'); grab(err) }))
    }
    if (approversChanged) {
      jobs.push(updateApprovers({ taskId: full.id, approverIds: cleanApprovers }).catch(err => { failed.push('approvers'); grab(err) }))
    }
    if (Object.keys(patch).length) {
      jobs.push(updateTask({ id: full.id, ...patch }).catch(err => { failed.push('details'); grab(err) }))
    }

    await Promise.all(jobs)
    setSaving(false)

    if (failed.length) {
      toast.error(serverMsg || `Some changes could not be saved (${failed.join(', ')}). Please try again.`)
    } else {
      toast.success('Task updated.')
      onSaved?.()
      onClose()
    }
  }

  const handleInviteExternal = async () => {
    const email = extEmail.trim()
    if (!email || !full) return
    if (externalAssignees.some(e => (e.email || '').toLowerCase() === email.toLowerCase())) {
      toast.info('That person has already been invited to this task.')
      return
    }
    setInvitingExt(true)
    try {
      const res = await assignExternalUser({ taskId: full.id, email, name: extName.trim() || null })
      const updated = res?.data?.data?.externalAssignees
      setExternalAssignees(Array.isArray(updated) ? updated : [...externalAssignees, { id: email, email, name: extName.trim() || null }])
      setExtEmail('')
      setExtName('')
      toast.success(`Invitation emailed to ${maskEmail(email)}.`)
    } catch {
      toast.error('Could not send that invitation. Please check the email address and try again.')
    } finally {
      setInvitingExt(false)
    }
  }

  const handleRemoveExternal = async assignee => {
    if (!full) return
    const prev = externalAssignees
    setExternalAssignees(prev.filter(e => e.id !== assignee.id))
    try {
      const res = await removeExternalAssignee({ taskId: full.id, externalAssigneeId: assignee.id })
      const updated = res?.data?.data?.externalAssignees
      if (Array.isArray(updated)) setExternalAssignees(updated)
    } catch {
      setExternalAssignees(prev)
      toast.error('Could not remove that person. Please try again.')
    }
  }

  const selectedContractor = contractors.find(c => c.id === contractorId)
  const contractorNoEmail = !!contractorId && !!selectedContractor && !selectedContractor.email

  const tasksHref = `/tasks${bpId ? `?bpId=${bpId}` : ''}`
  const fullHref = `/tasks/${task.id}?from=programme${bpId ? `&bpId=${bpId}` : ''}`

  return createPortal(
    <Backdrop onClick={onClose}>
      <Panel role='dialog' aria-label='Quick edit task' aria-modal='true' onClick={e => e.stopPropagation()}>
        <Header>
          <HeaderText>
            <Kicker>Quick edit</Kicker>
            <Title>{task.title || full?.title || 'Task'}</Title>
            <Sub>Update who does this work. Without leaving the compliance tracker.</Sub>
          </HeaderText>
          <CloseBtn type='button' onClick={onClose} aria-label='Close'><CloseRoundedIcon /></CloseBtn>
        </Header>

        <Body>
          {loading ? (
            <Skeleton aria-label='Loading task details'>
              {[0, 1, 2, 3].map(i => (
                <SkelField key={i}><SkelLabel /><SkelInput /></SkelField>
              ))}
            </Skeleton>
          ) : (
            <>
              <Field>
                <FieldLabel><PeopleAltRoundedIcon style={{ fontSize: 16 }} /> Assigned to</FieldLabel>
                <FieldHint>The people responsible for carrying out this task.</FieldHint>
                <UserPicker
                  value={assigneeIds}
                  onChange={setAssigneeIds}
                  buildingPassportId={bpId}
                  initialUsers={initialAssignees}
                  placeholder='Search people to assign…'
                />
                {contractorId && (
                  <AssignNote>A contractor is set for this task (below) and will be emailed it. Adding a team member here is optional.</AssignNote>
                )}
              </Field>

              <Field>
                <FieldLabel><FactCheckRoundedIcon style={{ fontSize: 16 }} /> Approver(s)</FieldLabel>
                <FieldHint>Who checks and signs the task off once the work is done.</FieldHint>
                <UserPicker
                  value={approverIds}
                  onChange={setApproverIds}
                  buildingPassportId={bpId}
                  initialUsers={initialApprovers}
                  placeholder='Search people to approve…'
                />
                {approverIds.some(id => assigneeIds.includes(id)) && (
                  <AssignNote>The same person is set as both an assignee and an approver. They’d be approving their own work. You can still save.</AssignNote>
                )}
              </Field>

              <Field>
                <FieldLabel><DescriptionRoundedIcon style={{ fontSize: 16 }} /> Form</FieldLabel>
                <FieldHint>The form people fill in to complete this task. Without a form, the task cannot be completed or signed off.</FieldHint>
                <FormTemplateField
                  value={formTemplateId}
                  template={selectedTemplate?.id === formTemplateId ? selectedTemplate : null}
                  onChange={template => setFormTemplateId(template?.id || '')}
                  buildingPassportId={bpId}
                  programmeObligationId={full?.metadata?.programmeObligationId || task?.metadata?.programmeObligationId}
                  allowNone
                  emptyLabel='No form chosen yet'
                  emptyHint='Without a form, nobody can complete or sign off this task.'
                />
                {!formTemplateId && (
                  <NoFormNote>⚠ This task has no form, so no one can complete or sign it off. Choose a form to unblock it.</NoFormNote>
                )}
              </Field>

              <Field>
                <FieldLabel><EngineeringRoundedIcon style={{ fontSize: 16 }} /> Contractor</FieldLabel>
                <FieldHint>The company carrying out the work. They're emailed the task, so they need an email address on file before they can be assigned.</FieldHint>
                <SearchableSelect
                  value={contractorId || null}
                  onChange={v => {
                    const c = contractors.find(x => String(x.id) === String(v))
                    if (c && c.assignmentBlocked && c.id !== contractorId) return
                    setContractorId(v || '')
                  }}
                  options={contractors.map(c => {
                    const blocked = c.assignmentBlocked && c.id !== contractorId
                    const why = !c.email ? 'no email on file' : 'unavailable'
                    return { value: c.id, label: c.name, sublabel: blocked ? why : undefined }
                  })}
                  clearable
                  clearLabel='No contractor'
                  placeholder='No contractor'
                  searchPlaceholder='Search contractors…'
                  ariaLabel='Contractor'
                />
                {contractorNoEmail && (
                  <ContractorWarn>
                    ⚠ {selectedContractor.name} has no email address, so they can't be emailed this task. Add an email on the contractor's page so they're notified.
                  </ContractorWarn>
                )}
              </Field>

              <Field>
                <FieldLabel><MailOutlineRoundedIcon style={{ fontSize: 16 }} /> Invite someone by email</FieldLabel>
                <FieldHint>Send this task to someone who isn't on the platform. They'll get an email with a secure link to view and complete it. No account needed.</FieldHint>
                {externalAssignees.length > 0 && (
                  <ChipRow>
                    {externalAssignees.map(e => (
                      <Chip key={e.id}>
                        <span>{e.name || maskEmail(e.email)}</span>
                        <ChipX type='button' onClick={() => handleRemoveExternal(e)} aria-label={`Remove ${e.name || maskEmail(e.email)}`}>
                          <CloseRoundedIcon style={{ fontSize: 13 }} />
                        </ChipX>
                      </Chip>
                    ))}
                  </ChipRow>
                )}
                <ExtRow>
                  <ExtInput type='email' placeholder='name@company.com' value={extEmail} onChange={e => setExtEmail(e.target.value)} />
                  <ExtInput placeholder='Name (optional)' value={extName} onChange={e => setExtName(e.target.value)} />
                  <ExtBtn type='button' disabled={!extEmail.trim() || invitingExt} onClick={handleInviteExternal}>
                    {invitingExt ? 'Sending…' : 'Invite'}
                  </ExtBtn>
                </ExtRow>
              </Field>

              <Field>
                <FieldLabel><EventRoundedIcon style={{ fontSize: 16 }} /> Due date</FieldLabel>
                <FieldHint>When this task needs to be completed by.</FieldHint>
                <DateInput type='date' value={deadline} onChange={e => setDeadline(e.target.value)} />
              </Field>

              <BulkHint>
                Need to update several tasks at once?{' '}
                <BulkLink to={tasksHref} onClick={onClose}>
                  Open Tasks <OpenInNewRoundedIcon style={{ fontSize: 13 }} />
                </BulkLink>
                {' '}— there you can tick multiple tasks and assign them to a person all at once.
              </BulkHint>
            </>
          )}
        </Body>

        <Footer>
          <FullLink to={fullHref} onClick={onClose}>Open full task page</FullLink>
          <FooterRight>
            <Cancel type='button' onClick={onClose}>Cancel</Cancel>
            <Save type='button' disabled={loading || saving} onClick={save}>
              {saving ? 'Saving…' : 'Save changes'}
            </Save>
          </FooterRight>
        </Footer>
      </Panel>
    </Backdrop>,
    document.body,
  )
}

export default TaskQuickEditModal

const fadeIn = keyframes`from { opacity: 0 } to { opacity: 1 }`
const rise = keyframes`from { opacity: 0; transform: translateY(10px) } to { opacity: 1; transform: translateY(0) }`
const pulse = keyframes`0% { opacity: 0.55 } 50% { opacity: 1 } 100% { opacity: 0.55 }`

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 100020;
  background: rgba(12, 18, 41, 0.42);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 40px 16px;
  overflow-y: auto;
  animation: ${fadeIn} 0.16s ease both;
`

const Panel = styled.div`
  width: 100%;
  max-width: 520px;
  background: #fff;
  border: 1px solid #ececec;
  border-radius: 16px;
  box-shadow: 0 24px 60px -28px rgba(12, 18, 41, 0.45);
  display: flex;
  flex-direction: column;
  animation: ${rise} 0.2s cubic-bezier(0.22, 0.61, 0.36, 1) both;
`

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid #f0f0f2;
`

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`

const Kicker = styled.span`
  display: block;
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.secondary};
`

const Title = styled.h2`
  margin: 4px 0 0;
  font-size: 17px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
  line-height: 1.3;
`

const Sub = styled.p`
  margin: 5px 0 0;
  font-size: 12.5px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const CloseBtn = styled.button`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  border-radius: 10px;
  background: #fff;
  color: ${({ theme }) => theme.colors.grey[800]};
  cursor: pointer;

  &:hover { border-color: ${({ theme }) => theme.colors.secondary}; color: ${({ theme }) => theme.colors.secondary}; }
`

const Body = styled.div`
  padding: 18px 20px 8px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.haiti};
`

const FieldHint = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const NoFormNote = styled.span`
  margin-top: 2px;
  font-size: 12px;
  font-weight: 700;
  color: #c2410c;
`

const AssignNote = styled.span`
  margin-top: 2px;
  font-size: 12px;
  font-style: italic;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const ContractorWarn = styled.span`
  margin-top: 2px;
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
  color: #c2410c;
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px 4px 10px;
  background: #eef2fb;
  border: 1px solid #dce4f2;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.haiti};
`

const ChipX = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: ${({ theme }) => theme.colors.grey[800]};
  cursor: pointer;

  &:hover { background: #d9e1f0; color: ${({ theme }) => theme.colors.haiti}; }
`

const ExtRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`

const ExtInput = styled.input`
  flex: 1 1 140px;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  border-radius: 10px;
  font: inherit;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};

  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`

const ExtBtn = styled.button`
  flex: 0 0 auto;
  padding: 9px 16px;
  border: none;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.secondary};
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: default; }
`

const DateInput = styled.input`
  padding: 10px 12px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  border-radius: 10px;
  font: inherit;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.haiti};

  &:focus { outline: none; border-color: ${({ theme }) => theme.colors.primary}; }
`

const BulkHint = styled.div`
  margin-top: 2px;
  padding: 12px 14px;
  background: #f6f8fb;
  border: 1px solid #e7edf5;
  border-radius: 10px;
  font-size: 12.5px;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.grey[800]};
`

const BulkLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;

  &:hover { text-decoration: underline; }
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 20px 18px;
  border-top: 1px solid #f0f0f2;
`

const FullLink = styled(Link)`
  font-size: 12.5px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.grey[800]};
  text-decoration: none;

  &:hover { color: ${({ theme }) => theme.colors.secondary}; text-decoration: underline; }
`

const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const Cancel = styled.button`
  padding: 9px 16px;
  border: 1px solid ${({ theme }) => theme.colors.grey[500]};
  border-radius: 999px;
  background: #fff;
  color: ${({ theme }) => theme.colors.grey[800]};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
`

const Save = styled.button`
  padding: 9px 18px;
  border: none;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;

  &:disabled { opacity: 0.6; cursor: default; }
`

const Skeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`

const SkelField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const SkelLabel = styled.div`
  width: 120px;
  height: 12px;
  border-radius: 6px;
  background: #eef0f3;
  animation: ${pulse} 1.4s ease-in-out infinite;
`

const SkelInput = styled.div`
  width: 100%;
  height: 42px;
  border-radius: 10px;
  background: #eef0f3;
  animation: ${pulse} 1.4s ease-in-out infinite;
`
